import { Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import toast from '../../utils/toast';
import { performLogout } from '@/services/logoutService';
import { handleDRFValidationErrors } from '@/utils/errorParser';
import { STORAGE_KEYS } from '@/utils/storageKeys';

export const errorMiddleware: Middleware = () => (next) => (action) => {
  if (!isRejectedWithValue(action)) {
    return next(action);
  }

  // Skip error handling on invitation page or when invitation params are present
  const params = new URLSearchParams(window.location.search);
  const hasInvitationParams = params.has('invitation') && params.has('organization');
  if (window.location.pathname === '/invite' || hasInvitationParams) {
    return next(action);
  }

  const status: number | undefined =
    (action as any)?.meta?.baseQueryMeta?.response?.status;

  const payload = action.payload as any;
  const data: any = payload?.data || payload || {};

  // Log error details for debugging (matches backend error_type and status_code)
  if (data?.error_type) {
    console.error(`Backend Error [${data.error_type}]:`, data);
  }

  // Handle DRF validation errors (400 status)
  if (status === 400 && data && typeof data === 'object') {
    const handled = handleDRFValidationErrors(data);
    if (handled) return next(action);
  }

  // Extract generic message from backend response
  const message = data?.detail || null;

  // Status-based fallback handling
  switch (status) {
    case 400:
      toast.error(message || 'Bad request. Please check your input.', 3000);
      break;

    case 401: {
      // Check if we're in the initial authentication phase
      const isInitialAuth = !localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
                           sessionStorage.getItem(STORAGE_KEYS.INITIAL_AUTH_PHASE) === 'true';

      // Be more lenient during initial authentication
      const maxFailures = isInitialAuth ? 5 : 2;

      const failureCount = parseInt(
        sessionStorage.getItem(STORAGE_KEYS.AUTH_FAILURE_COUNT) || '0',
        10
      );

      sessionStorage.setItem(
        STORAGE_KEYS.AUTH_FAILURE_COUNT,
        (failureCount + 1).toString()
      );

      if (failureCount >= maxFailures) {
        toast.error(
          message || 'Session expired. Please log in again.',
          3000
        );
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_FAILURE_COUNT);
        sessionStorage.removeItem(STORAGE_KEYS.INITIAL_AUTH_PHASE);
        performLogout();
      } else {
        // Only show warning after 2 failures to reduce noise
        if (failureCount >= 1) {
          toast.warning('Authentication issue detected.', 2000);
        }
      }
      break;
    }

    case 403:
      toast.error(
        message || 'You do not have permission to perform this action.',
        3000
      );
      break;

    case 404:
      toast.error(
        message || 'The requested resource was not found.',
        3000
      );
      break;

    case 429:
      toast.error(
        message || 'Too many requests. Please try again later.',
        3000
      );
      break;

    default:
      if (status && status >= 500) {
        toast.error(
          message || 'Server error occurred. Please try again later.',
          3000
        );
      } else {
        toast.error(message || 'An error occurred. Please try again', 3000);
      }
      break;
  }

  return next(action);
};
